import { useEffect, useState, useRef } from 'react';
import KickImage from 'Reuseables/KickImage';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  API_BASE_URL
} from '../constants';

//Helper
import { printErrorNew } from "../utils/Helpers";

/**
 * Hook that alerts clicks outside of the passed ref
 * @param {any} ref component.
 * @param {any} callback function.
 */
export function useOutsideAlerter(ref: any, callback: () => any) {
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);
}

/**
 * Hook for saving scroll state on pushing to another routes
 */
export function useScrollMemory(): void {
  const history = useHistory();

  useEffect(() => {
    const { push, replace } = history;

    // Override the history PUSH method to automatically set scroll state.
    history.push = async (path: string) => {
      await localStorage.setItem('scroll', window.scrollY.toString());
      push(path);
    };

    // Override the history REPLACE method to automatically set scroll state.
    history.replace = async (path: string) => {
      await localStorage.setItem('scroll', window.scrollY.toString());
      replace(path);
    };

    // Listen for location changes and set the scroll position accordingly.
    history.listen((location, action) => {
      const previousScroll = localStorage.getItem('scroll') || '';
      setTimeout(async () => {
        window.scrollTo(0, action !== 'POP' ? 0 : parseInt(previousScroll));
        if (action === 'POP') {
          await localStorage.removeItem('scroll');
        }
      }, 2000);
    });

    // Unregister listener when component unmounts.
    // return () => {
    //   unregister();
    // };
  }, [history]);
}

export function useOtpStorage(): any {
  const checkIfExpired = () => {
    const lastGetOtp = getFromLocalStorage()
    if (!lastGetOtp) {
      return true
    }

    const currentDate = Date.now();

    return lastGetOtp < currentDate;
  }

  const saveToLocalStorage = () => {
    const lastGetOtp = getFromLocalStorage()
    if (!lastGetOtp) {
      resendOtp()
      localStorage.setItem("last-get-otp", JSON.stringify(Date.now() + 300000));
      return null
    }

    const currentDate = Date.now();
    const isOtpExpired = lastGetOtp < currentDate;

    if (!isOtpExpired) {
      return null
    }

    resendOtp()
    localStorage.setItem("last-get-otp", JSON.stringify(Date.now() + 300000));
    return true
  }

  const getFromLocalStorage = () => {
    const lastGetOtp = localStorage.getItem("last-get-otp");
    if (!lastGetOtp) {
      return null
    }

    return Number(lastGetOtp)
  }

  const resendOtp = () => {
    const lastResend = localStorage.getItem("last-resend-otp");
    if (!lastResend) {
      localStorage.setItem("last-resend-otp", JSON.stringify(Date.now() + 60000));
      localStorage.setItem("last-get-otp", JSON.stringify(Date.now() + 300000));
      return null
    }

    const currentDate = Date.now();
    const isOtpExpired = Number(lastResend) < currentDate;

    if (!isOtpExpired) {
      return null
    }

    localStorage.setItem("last-get-otp", JSON.stringify(Date.now() + 300000));
    localStorage.setItem("last-resend-otp", JSON.stringify(Date.now() + 60000));
  }

  const getResendOtpTime = () => {
    const lastGetOtp = localStorage.getItem("last-resend-otp");
    if (!lastGetOtp) {
      return null
    }

    return Number(lastGetOtp)
  }

  const checkIfRsendOtpExpired = () => {
    const lastGetOtp = getResendOtpTime()
    if (!lastGetOtp) {
      return true
    }

    const currentDate = Date.now();

    return lastGetOtp < currentDate;
  }

  const saveResendOtpToLocalStorage = () => {
    const lastGetOtp = getResendOtpTime()

    if (!lastGetOtp) {
      localStorage.setItem("last-resend-otp", JSON.stringify(Date.now() + 60000));
      return null
    }

    const currentDate = Date.now();
    const isOtpExpired = lastGetOtp < currentDate;

    if (!isOtpExpired) {
      return null
    }

    localStorage.setItem("last-resend-otp", JSON.stringify(Date.now() + 60000));
    return true
  }

  const resetOtpFromLocalstorage = () => {
    localStorage.removeItem("last-resend-otp");
    localStorage.removeItem("last-get-otp");
  }
  return {
    saveToLocalStorage,
    getFromLocalStorage,
    checkIfExpired,
    resendOtp,
    checkIfRsendOtpExpired,
    resetOtpFromLocalstorage,
    getResendOtpTime,
    saveResendOtpToLocalStorage
  }
}

export function useEtaText(): any {
  const [etaText, setEtaText] = useState({})

  useEffect(() => {
    (async () => {
      await axios.get(`${API_BASE_URL}/settings?name=eta_text`)
        .then(res => setEtaText(res.data?.data?.data?.[0]?.value ?? {}))
        .catch(err => setEtaText({}))
    })();
  }, [])

  return [etaText]
}

export function usePrevious(value) {
  const ref = useRef<any>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function useToast() {

  const showToast = (msg: any, isError?: boolean) => {
    if (!msg) return;

    const config: any = {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined
    }

    let toastMsg;

    if (typeof msg === 'string') {
      toastMsg = msg
    } else if (isError) {
      toastMsg = printErrorNew(msg)
    }

    isError ? toast.error(toastMsg, config) : toast.success(toastMsg, config);
  }

  return showToast;
}